'use client';

/**
 * InterestCapture
 *
 * The email-list capture used to gather demand for the contributor / member /
 * coverage funnel BEFORE it goes live. Posts to /api/interest (public,
 * honeypot + rate-limited), which writes to `interest_signups` independently of
 * the WS-K funnel flags — safe to show while the rest of the funnel is dark.
 *
 * Two modes:
 *   - Standalone (showInterestPicker, default): renders the segmented-interest
 *     toggles so a visitor can self-select Contributing / Claiming / Coverage.
 *   - In-flow (showInterestPicker=false): the OnboardingFlow already knows the
 *     segment from the relationship fork, so the toggles are hidden and the
 *     interests are passed in fixed. Pair with `onSuccess` so the flow owns the
 *     celebratory finish screen instead of the inline success box.
 */

import { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const INTEREST_OPTIONS = [
  { id: 'contributing', label: 'Contributing' },
  { id: 'claiming', label: 'Claiming my project' },
  { id: 'coverage', label: 'Coverage' },
] as const;

type InterestId = (typeof INTEREST_OPTIONS)[number]['id'];

/**
 * Maps the landing page's qualifying-question segment → the interest funnels we
 * already track, so segment signups stay meaningful to interest-keyed views
 * while the precise segment rides along as `userType`.
 */
const SEGMENT_INTEREST: Record<string, InterestId[]> = {
  owner: ['claiming', 'coverage'],
  agency: ['coverage'],
  community: ['contributing'],
  investor: [],
};

interface InterestCaptureProps {
  /** Where this capture lives — segments the signup (e.g. 'post_submit', 'join'). */
  source: string;
  /** Optional: the project they just suggested/named, stored alongside the email. */
  entityName?: string;
  /** Pre-select interests (e.g. ['claiming'] on a founder-facing surface). */
  defaultInterests?: InterestId[];
  /**
   * Show the segmented-interest toggles. Default true (standalone). The
   * OnboardingFlow passes false — the fork already told us the segment.
   */
  showInterestPicker?: boolean;
  /**
   * Called after a successful submit. When provided, the inline success box is
   * NOT shown — the parent (OnboardingFlow) advances to its own finish screen.
   */
  onSuccess?: (email: string) => void;
  /** CTA label override (default "Join the list"). */
  submitLabel?: string;
  /**
   * Single-select "what best describes you" qualifying question (the
   * direct-response landing pattern). When provided, the multi-toggle interest
   * picker is replaced by one segment selector; the chosen segment posts as
   * `userType` and derives a sensible `interests` value via SEGMENT_INTEREST.
   * The first option is pre-selected so email stays the only required input.
   */
  segments?: { id: string; label: string }[];
  /** Label above the segment selector (default "What best describes you?"). */
  segmentQuestion?: string;
  /** Reassurance line under the form (privacy / no-spam microcopy). */
  note?: string;
}

export default function InterestCapture({
  source,
  entityName,
  defaultInterests = [],
  showInterestPicker = true,
  onSuccess,
  submitLabel = 'Join the list',
  segments,
  segmentQuestion = 'What best describes you?',
  note = 'No spam — just a heads-up when early access opens.',
}: InterestCaptureProps) {
  const inSegmentMode = Array.isArray(segments) && segments.length > 0;
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState<InterestId[]>(defaultInterests);
  const [segment, setSegment] = useState<string | null>(segments?.[0]?.id ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: InterestId) =>
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const interestsToSend = inSegmentMode
        ? (segment ? SEGMENT_INTEREST[segment] ?? [] : [])
        : interests;
      const res = await fetch('/api/interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          interests: interestsToSend,
          source,
          ...(entityName ? { entityName } : {}),
          ...(inSegmentMode && segment ? { userType: segment } : {}),
          website: '', // honeypot — real users leave empty
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? 'Something went wrong.');
      }
      // The flow owns the finish screen when onSuccess is provided; otherwise
      // fall back to the inline success box (standalone usage).
      if (onSuccess) onSuccess(email.trim());
      else setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex items-center gap-3 rounded border border-signal-green/35 bg-signal-green/10 px-4 py-4"
        data-testid="interest-capture-success"
        role="status"
      >
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-signal-green/20">
          <Check size={13} className="text-signal-green-text" />
        </span>
        <p className="text-[13.5px] text-parchment">
          <span className="font-semibold">You&apos;re on the list.</span>{' '}
          <span className="text-parchment-dim">We&apos;ll reach out when it opens.</span>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="interest-capture" className="space-y-3">
      {/* Honeypot — visually hidden */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: 'none' }}
        onChange={() => {}}
      />

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@project.xyz"
          aria-label="Email address"
          className="w-full flex-1 rounded border border-divider bg-ink px-3.5 py-2.5 text-sm text-parchment placeholder:text-parchment-dim/70 focus:border-scout-gold/60 focus:outline-none focus:ring-1 focus:ring-scout-gold/40"
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="group flex items-center justify-center gap-2 rounded bg-scout-gold px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:bg-scout-gold/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink/40 border-t-ink" />
          ) : (
            <>
              {submitLabel}
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </>
          )}
        </button>
      </div>

      {inSegmentMode ? (
        <fieldset className="space-y-2">
          <legend className="font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim/70">
            {segmentQuestion}
          </legend>
          <div className="flex flex-wrap gap-2">
            {segments!.map((opt) => {
              const on = segment === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSegment(opt.id)}
                  aria-pressed={on}
                  className={`rounded-full border px-3.5 py-1.5 text-[12.5px] transition-colors ${
                    on
                      ? 'border-scout-gold/60 bg-scout-gold-dim text-scout-gold'
                      : 'border-divider text-parchment-dim hover:border-slate-light/50 hover:text-parchment'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : (
        showInterestPicker && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim/70">
              I&apos;m interested in
            </span>
            {INTEREST_OPTIONS.map((opt) => {
              const on = interests.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggle(opt.id)}
                  aria-pressed={on}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] transition-colors ${
                    on
                      ? 'border-scout-gold/50 bg-scout-gold-dim text-scout-gold'
                      : 'border-divider text-parchment-dim hover:border-slate-light/50 hover:text-parchment'
                  }`}
                >
                  <span
                    className={`flex h-3 w-3 items-center justify-center rounded-[2px] border ${
                      on ? 'border-scout-gold bg-scout-gold' : 'border-slate-ds'
                    }`}
                  >
                    {on && <Check size={9} className="text-ink" strokeWidth={3.5} />}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        )
      )}

      {error && <p className="text-[12.5px] text-alert-red-text">{error}</p>}

      <p className="text-[11px] text-parchment-dim/60">{note}</p>
    </form>
  );
}
